import React, { useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { makeStyles, useTheme } from '@mui/styles'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'

import lang from 'lang'
import SubscriptionTitle from '@/modules/base/components/modal/upgrade-modal/subscription-title/subscription-title'
import { formatCurrency } from '@/modules/_helpers/formatCurrency'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import subscriptionPlanOrder from '@/modules/base/components/modal/upgrade-modal/subscription-selection/_helpers/mboSubscriptionPlanOrder'
import { createButtonLink } from '@/modules/moduleSubscription/components/_helpers/planCheckout'
import { usePlanCheckout } from '@/modules/moduleSubscription/components/_helpers/usePlanCheckout'

import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import IconCheck from '@assets/images/icons/check.svg'
import IconUncheck from '@assets/images/icons/close.svg'
import IconUnlimited from '@assets/images/icons/infinity.svg'
import IconUser from '@assets/images/customer-private-man2.svg'
import IconUsers from '@assets/images/customer-private-family2.svg'
import IconCompany from '@assets/images/company.svg'
import CloudIcon from '@assets/images/icons/cloud.svg'
import ScanIcon from '@assets/images/icons/ocr.svg'
import { ButtonSquare } from '@/modules/base/components/buttons'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import { useSelector } from 'react-redux'
import { ProductTypes } from '@/modules/base/util/constants'
import { withoutDemoSubscription } from '@/utils/shop-subscriptions'
import IconCheckCircle from '@assets/images/icons/check_circle.svg'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ThemeInterface from '@/theme-interface'
import { Box } from '@mui/material'

const iconConfig = {
  checked: IconCheck,
  unchecked: IconUncheck,
  partiallyChecked: CheckCircleOutlineRoundedIcon,
  unlimited: IconUnlimited,
  company: IconCompany,
  user: IconUser,
  users: IconUsers,
  main: CloudIcon,
  staff: IconUsers,
  document: ScanIcon,
}

const useStyles = makeStyles((theme) => ({
  container: ({ disabled }) => ({
    opacity: disabled ? '50%' : 'unset',
  }),
  subscriptionTitle: {
    fontSize: 36,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    fontWeight: theme.fontWeights.semiBold,
  },
  line: {
    borderBottom: theme.border,
  },
  couponWrapper: {
    backgroundColor: '#c7d4e4',
    padding: '8px 16px',
    borderRadius: '50px',
    margin: '10px 0',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    gap: 10,
  },
  tooltip: {
    fontSize: theme.fontSizes.default,
    color: theme.black,
    maxWidth: 400,
  },
  tooltipWrapper: {
    width: '100%',
    justifyContent: 'flex-start',
    paddingRight: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 0,
  },
  contentWrapper: ({ itemCount }) => ({
    color: theme.text,
    display: 'flex',
    alignItems: 'center',
    flexBasis: itemCount === 1 ? '100%' : `${90 / itemCount}%`,
    justifyContent: 'center',
    flexDirection: 'column',
    maxWidth: 500,
    position: 'relative',
    background: 'none',
    borderRadius: 8,
    minHeight: 340,
  }),
  active: {
    borderColor: theme.primary,
  },
  buttonEnd: {
    height: '100%',
    justifyContent: 'flex-end',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    padding: 20,
    width: '100%',
    flex: '1 0 auto',
    minHeight: 240,
    border: '1px solid rgb(222, 226, 237)',
    borderRadius: '0 0 8px 8px',
    borderTop: 'none',
  },
  header: {
    border: '1px solid rgb(222, 226, 237)',
    borderRadius: '8px 8px 0 0',
    paddingLeft: 18,
    flexDirection: 'column',
    alignItems: 'flex-start',
    height: 'fit-content',
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    fontSize: 15,
    padding: '15px 0',
    backgroundColor: theme.backgroundColorBeige,
    backgroundImage: 'none',
    color: theme.checkboxColors.label,
    borderBottom: theme.border,
  },
  details: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: '1rem',
  },
  itemWrapper: {
    display: 'flex',
    // justifyContent: 'space-between',
    width: '100%',
  },
  itemText: {
    fontWeight: theme.fontWeights.bold,
  },
  itemButton: {
    maxWidth: 175,
    backgroundColor: theme.checkboxColors.label,
    borderRadius: 20,
    margin: 0,
  },
  sectionWrapper: {
    padding: '0 5px',
    paddingTop: 15,
    width: '100%',
    gap: 10,
    display: 'flex',
    flexDirection: 'column',
  },
  sectionHeadline: {
    fontWeight: theme.fontWeights.bold,
  },
  tooltipButtonWrapper: {
    width: '100%',
    maxWidth: 175,
  },
  column: {
    paddingRight: 5,
    minHeight: 20,
  },
  htmlColumnValue: {
    '& > span': {
      display: 'inline-block',
    },
  },
  marketPlaceForm: {
    margin: 0,
  },
  planPrice: {
    flexDirection: 'column',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    fontSize: theme.fontSizes.smedium,
    marginTop: 0,
    marginBottom: 10,
    columnGap: 10,
  },
  activeButton: {
    background: theme.planUpgradeModal.colors.blue,
    gap: 5,
  },
  buttonIcon: {
    fill: theme.white,
  },
  icon: {
    verticalAlign: 'middle',
  },
  planPriceOld: {
    color: theme.red,
    textDecoration: 'line-through',
  },
  priceBold: {
    fontWeight: theme.fontWeights.medium,
  },
  priceHuge: {
    fontSize: theme.fontSizes.huge,
  },
  tooltipIconOutlined: {
    height: 16,
    width: 16,
    fill: theme.text,
    opacity: 0.7,
  },
  reducedPriceBadge: ({ disabled }) => ({
    background: '#e9435c',
    padding: '5px 30px',
    color: theme.white,
    width: 'max-content',
    fontSize: theme.fontSizes.smaller,
    paddingBottom: 15,
    marginBottom: -12,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    opacity: disabled ? '50%' : 'unset',
  }),
  marketplacePlanBuyButton: {
    // margin: '0 auto',
  },
  label: {
    margin: 0,
    width: '100%',

    '& > span': {
      paddingRight: 5,
    },
  },
  expandableLabel: {
    cursor: 'pointer',
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    userSelect: 'none',
  },
  labelGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: theme.cyanMint,
    color: theme.darkCyan,
    fontSize: theme.fontSizes.tiny,
    fontWeight: theme.fontWeights.semiBold,
    padding: '2px 10px',
    borderRadius: 999,
    lineHeight: 1.4,
    whiteSpace: 'nowrap',
    marginLeft: 4,
  },
  expandIcon: {
    height: 18,
    width: 18,
    fill: theme.text,
    opacity: 0.7,
  },
  disabled: {
    opacity: '50%',
  },
  listItems: {
    width: '100%',
    listStylePosition: 'inside',
    margin: '4px 0 0 0',
    padding: 0,
    fontSize: theme.fontSizes.small,
    color: theme.text,
  },
  listItem: {
    marginLeft: 8,
  },
  copyIcon: {
    marginBottom: 1,
  },
}))

const ItemLabel = ({ label, tooltipHtml, listItems, badge, isNew, classes, theme, getIcon }) => {
  const [isOpen, setIsOpen] = useState(false)
  const hasListItems = Array.isArray(listItems) && listItems.length > 0
  const tooltipIcon = <InfoOutlinedIcon className={clsx(classes.icon, classes.tooltipIconOutlined)} />

  const labelGroup = (
    <span className={classes.labelGroup}>
      <span>{label || ''}</span>
      {tooltipHtml && <RichTooltip content={<div className={classes.tooltip} dangerouslySetInnerHTML={{ __html: tooltipHtml }} />} width='auto' padding={10} placement={'top'}>
        {tooltipIcon}
      </RichTooltip>}
      {isNew && <span className={classes.badge} style={{ backgroundColor: theme.green2, color: theme.white }}>NEU</span>}
      {badge && <span className={classes.badge}>{badge}</span>}
    </span>
  )

  return (
    <Box className={clsx(classes.column, tooltipHtml && classes.tooltipWrapper)} width='100%' sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <p className={classes.label}>
        {hasListItems
          ? (
            <span className={classes.expandableLabel} onClick={() => setIsOpen(open => !open)}>
              {labelGroup}
              {isOpen
                ? <ExpandLessIcon className={classes.expandIcon} />
                : <ExpandMoreIcon className={classes.expandIcon} />}
            </span>
          )
          : labelGroup}
      </p>
      {hasListItems && isOpen && (
        <ul className={classes.listItems}>
          {listItems.map((li, idx) => (
            <li key={`li-${idx}`} className={classes.listItem} >
              <div className={clsx(classes.itemWrapper, li?.value?.value === 'unchecked' && classes.disabled)}>
                {li?.value?.type === 'icon' && <div className={classes.column}>{getIcon(li.value.value)}</div>}
                <ItemLabel label={li.label} tooltipHtml={li.tooltipHtml} badge={li.badge} isNew={li.isNew} listItems={[]} classes={classes} theme={theme} getIcon={getIcon} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Box>
  )
}

const SubscriptionItem = props => {
  const { itemCount, plan, plansHaveReducedPrice, serial, relatedSubscription, disabled, typeOfSubscription, upgradeLink } = props
  const theme: ThemeInterface = useTheme()

  const activeSubscriptions = withoutDemoSubscription(useSelector(state => state.subscription.details.subscriptions))

  const currentSubscriptionPlan = activeSubscriptions?.find(sub => {
    const plans = subscriptionPlanOrder[typeOfSubscription]?.filter(item => item.key === plan.name)
    const planKeyNames = plans?.map(item => item.name) || []
    return planKeyNames.includes(sub.type)
  })
  const isSubscriptionPlanActive = Boolean(currentSubscriptionPlan) && !(currentSubscriptionPlan.isExpired || currentSubscriptionPlan.isCancelled)
  const isSubscriptionPlanTrial = activeSubscriptions.find(sub => sub.typeOfSubscription === typeOfSubscription)?.isTrial
  const isSubscriptionExpiredOrCancelled = currentSubscriptionPlan?.isExpired || currentSubscriptionPlan?.isCancelled
  const expiredOrCancelledSubsExists = activeSubscriptions.find(sub => {
    return sub.typeOfSubscription === typeOfSubscription && !sub.isTrial && (sub.isExpired || sub.isCancelled)
  })
  const isDisabled = disabled || (expiredOrCancelledSubsExists && expiredOrCancelledSubsExists?.id !== currentSubscriptionPlan?.id)

  const reducedPricePercent = plan.reducedPrice && Math.round(((plan.price - plan.reducedPrice) * 100) / plan.price)

  const [isRedirecting, setIsRedirecting] = useState(false)

  const classes = useStyles({ bestseller: plan.bestseller, itemCount, plansHaveReducedPrice, hasPremiumPlan: !!relatedSubscription, disabled: isDisabled })

  const { checkout, marketplaceForm } = usePlanCheckout(classes.marketPlaceForm)

  const getIcon = (value) => {
    return (
      <SVGIcon
        svg={iconConfig[value]}
        height={['unchecked', 'checked', 'partiallyChecked'].includes(value) ? '18px' : '20px'}
        width={['unchecked', 'checked', 'partiallyChecked'].includes(value) ? '18px' : '20px'}
        fill={value === 'unchecked' ? theme.red : value === 'partiallyChecked' ? theme.orange : theme.green2}
        style={{ color: value === 'unchecked' ? theme.red : value === 'partiallyChecked' ? theme.orange : theme.green2, width: 18, height: 18 }}
      />
    )
  }

  const getElementOfItem = (item, key) => {
    const { label, value: valueItem, tooltipHtml, listItems, badge, isNew } = item
    const { type, value } = valueItem || {}

    const labelEle = type !== 'button'
      ? <ItemLabel label={label} tooltipHtml={tooltipHtml} listItems={listItems} badge={badge} isNew={isNew} classes={classes} theme={theme} getIcon={getIcon} />
      : null

    return (
      <div key={key} className={clsx(classes.itemWrapper, value === 'unchecked' && classes.disabled)}>
        {type === 'icon' && <div className={classes.column}>
          {getIcon(value)}
        </div>}
        {labelEle}
        {type === 'text' && <div className={clsx(classes.itemText, classes.column)}>{value}</div>}
        {type === 'html' && <div className={clsx(classes.itemText, classes.column, classes.htmlColumnValue)} dangerouslySetInnerHTML={{ __html: value }} />}
      </div>
    )
  }

  let couponEle
  if (plan.couponCode?.code) {
    couponEle = (<div
      className={classes.couponWrapper}
      onClick={() => navigator.clipboard.writeText(plan.couponCode.code)}
    >mit Rabattcode: <b>{plan.couponCode.code}</b><ContentCopyIcon className={classes.copyIcon} fontSize='small' />
    </div>)
  }

  const buttonEle = (itemButton) => {
    const { value: { type, value, metaData } } = itemButton
    const label = isSubscriptionPlanActive
      ? lang.started
      : isSubscriptionExpiredOrCancelled
        ? lang.generics.withdrawCancellation
        : itemButton.label

    const shouldRender = type !== 'button' || (
      (metaData.type === 'new_purchase' && (!relatedSubscription || relatedSubscription?.isExpired))
      || (metaData.type === 'upgrade' && !!relatedSubscription && !relatedSubscription?.isExpired)
      || !['new_purchase', 'upgrade'].includes(metaData.type)
    )

    const buttonLink = type === 'button' && shouldRender ? createButtonLink({ typeOfSubscription, serial, relatedSubscription, upgradeLink, value }) : ''
    const buttonIcon = isSubscriptionPlanActive && <SVGIcon className={classes.buttonIcon} width='13px' height='13px' svg={IconCheckCircle} />

    let button = null
    if (shouldRender) {
      button = (<ButtonSquare
        disabled={isDisabled || !buttonLink || isRedirecting}
        customClass={clsx(
          classes.itemButton,
          isSubscriptionPlanActive && !isSubscriptionExpiredOrCancelled && classes.activeButton,
          isSubscriptionPlanTrial && classes.activeButton,
        )}
        onClick={() => {
          try {
            setIsRedirecting(true)
            checkout(buttonLink, { isMarketplace: typeOfSubscription === ProductTypes.MAIN && metaData.shopType === 'marketplace' })
          } catch {
            setIsRedirecting(false)
          }
        }}
        variant={'small'}
      >
        {label}
        {buttonIcon}
      </ButtonSquare>)
      if (!buttonLink) {
        button = (
          <RichTooltip content={<div className={classes.tooltip}>{lang.upgradeModal.upgradeNotAvailable}</div>} placement={'top'}>
            <div className={classes.tooltipButtonWrapper}>
              {button}
            </div>
          </RichTooltip>
        )
      }
    }

    return (
      <>
        {button}
        {typeOfSubscription === ProductTypes.MAIN && type === 'button' && metaData.shopType === 'marketplace' && marketplaceForm}
      </>
    )
  }

  return (
    <div className={classes.container}>
      {reducedPricePercent
        && <div className={classes.reducedPriceBadge}>
          {reducedPricePercent}% {lang.generics.discount}
        </div>
      }
      <div className={clsx(classes.contentWrapper, classes.active)}>
        <div className={classes.header}>
          <SubscriptionTitle htmlTitle={plan.headline} className={classes.subscriptionTitle} />
          <p className={classes.planPrice}>
            {plan.reducedPrice
              ? <>
                <span className={classes.planPriceOld}><span className={classes.priceBold}>{formatCurrency(plan.price)}</span> {plan.legalPriceMessageHtml?.html}</span>
                <span><span className={clsx(classes.priceBold, classes.priceHuge)}>{formatCurrency(plan.reducedPrice)}</span> {plan.legalPriceMessageHtml?.html}<sup>1)</sup></span>
              </>
              : <span><span className={clsx(classes.priceBold, classes.priceHuge)}>{formatCurrency(plan.price)}</span> {plan.legalPriceMessageHtml?.html}<sup>1)</sup></span>
            }
          </p>
          {couponEle}
          {plan.sections?.length && <div className={classes.marketplacePlanBuyButton}>
            {plan.sections.at(-1).items.map((item, index) => <React.Fragment key={`button-${index}`}>{buttonEle(item)}</React.Fragment>)}
          </div>}
        </div>
        <div className={classes.content}>
          <div className={classes.details}>
            {plan.sections.map((section, index) => {
              const { headline, lineAbove } = section
              const itemElements = section.items.map((item, itemIndex) => getElementOfItem(item, `item-${index}-${itemIndex}`))

              const justifyButtonEnd = index + 1 === plan.sections.length && section.items.some(i => i?.value?.type === 'button')
              return (
                <div key={`section-${index}`} className={clsx(classes.sectionWrapper, justifyButtonEnd ? classes.buttonEnd : '')}>
                  {lineAbove && <span className={classes.line}></span>}
                  {headline && <div className={classes.sectionHeadline}>{headline}</div>}
                  {itemElements}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

SubscriptionItem.propTypes = {
  active: PropTypes.bool,
  disabled: PropTypes.bool,
  serial: PropTypes.string,
  typeOfSubscription: PropTypes.string,
  onClick: PropTypes.func,
  period: PropTypes.string,
  relatedSubscription: PropTypes.object,
  upgradeLink: PropTypes.string,
  plan: PropTypes.object,
}

export default SubscriptionItem
